import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import cipherLogo from './assets/cipher-icon-only-transparent.png';
import loginHeroMobile from './assets/login-hero-mobile.jpg';
import loginHeroDesktop from './assets/login-hero-desktop.jpg';
import { Target, Dumbbell, TrendingUp, Trophy } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import OnboardingFlow from './OnboardingFlow';
import AdminPanel from './AdminPanel';
import LoginPage from './LoginPage';
import ResetPassword from './ResetPassword';
import ClientProgramView from './ClientProgramView';
import EnhancedStats from './components/EnhancedStats';
import ApprovalPending from './components/ApprovalPending';
import PrivacySettings from './components/PrivacySettings';
import HealthSharingPrompt from './components/HealthSharingPrompt';
import CoachMessagesScreen from './components/CoachMessagesScreen';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import PaymentGate from './components/PaymentGate';
import FitnessBridgeSetup from './components/FitnessBridgeSetup';
import BottomNav from './components/BottomNav';
import DesktopTopNav from './components/DesktopTopNav';
import SupportModal from './components/SupportModal';
import PushNotificationPrompt from './components/PushNotificationPrompt';
import LoadingScreen, { RevealOnMount } from './components/LoadingScreen';
import ServerUnreachable from './components/ServerUnreachable';
import SubscriptionEndingNotice from './components/SubscriptionEndingNotice';
import { statusGivesAccess } from './utils/paymentAccess';
import { subscriptionEndingNotice } from './utils/subscriptionEnding';
import { supabase, signOutOfThisDevice } from './supabase';
import { restartApp } from './utils/restartApp';
import { couldNotReach, authCouldNotReach } from './utils/reachability';
import { toastSuccess, toastError } from './ui/toast';
import { signInWithGoogle } from './utils/googleAuth';
import { useUserRole } from './hooks/useUserRole';
import { gateView, standingFor } from './utils/viewGate';
import { nutritionJoinedOnThisDevice, goToJoinedNutrition, wantsToJoin, clearJoinIntent } from './utils/crossAppSwitch';
import { markStartup, STEPS } from './utils/startupTrace';
import { colors as theme } from './theme';

// Origins Nutrition can legitimately postMessage a session from — mirrors
// Nutrition's own FITNESS_ORIGINS allowlist in app/_layout.tsx.
const NUTRITION_ORIGINS = [
  'https://nutrition.cipher-health.com',
  'https://cipher-nutrition.vercel.app',
];

const colors = {
  primary: theme.gold,
  bg: theme.bg,
  bgCard: theme.surface1,
  border: theme.borderStrong,
  text: theme.text,
};

// Views a logged-in user can be restored to after a refresh/deep-link.
// Auth always runs first — the URL only acts as a hint for valid dashboard destinations.
const RESTORABLE_VIEWS = new Set(['stats', 'privacy']);

// Views a signed-in person is working in. If their account is checked again while they are on one
// of these and the server does not answer, they stay where they are.
const WORKING_VIEWS = new Set(['dashboard', 'stats', 'messages', 'privacy', 'admin']);

// How long the check of who is signed in may take before the app says it cannot reach the server.
// The check carries on behind that screen. Normally it takes well under a second; with a token
// that has run out and no server, the Supabase client alone spends about 50 seconds retrying
// before it gives up, and until this the person watched the logo for all of them.
const CANNOT_REACH_AFTER_MS = 12000;

// Maps every top-level `view` value to a real URL and back. This is a thin routing layer
// over the existing view-state machine below, not a replacement for it: `view` and every
// setView()/navigateTo() call site stay exactly as they were, so none of the async auth/
// payment-gating logic changes. The two effects further down keep the URL and `view` in
// sync in both directions (view change -> pushes a history entry; Back/Forward -> updates
// `view`), which is what actually fixes "Back is broken, no deep links" (item #4).
const VIEW_TO_PATH = {
  loading: '/',
  home: '/',
  onboarding: '/onboarding',
  login: '/login',
  'fitness-bridge': '/fitness-bridge',
  'payment-confirming': '/payment-confirming',
  paywall: '/paywall',
  dashboard: '/dashboard',
  stats: '/stats',
  'privacy-policy': '/privacy-policy',
  terms: '/terms',
  privacy: '/privacy',
  admin: '/admin',
  'pending-approval': '/pending-approval',
  suspended: '/suspended',
  rejected: '/rejected',
  'reset-password': '/reset-password',
};
const PATH_TO_VIEW = Object.fromEntries(
  Object.entries(VIEW_TO_PATH)
    .filter(([v]) => v !== 'loading') // 'home' already owns '/', keep the map one-to-one
    .map(([v, p]) => [p, v])
);

function AppRouter() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}

// Toast + PWA install prompt + offline banner - rendered at app root level.
// Defined at module scope (not inside App) so it keeps a stable component
// identity across renders — otherwise React treats it as a new component
// type on every App re-render and force-remounts <Toaster>, which races
// react-hot-toast's own exit-animation timers and throws a removeChild
// NotFoundError when both try to clean up the same toast DOM node.
// The home-screen guide lives in ClientProgramView, which knows when a workout is idle.
const ToastContainer = ({ isOnline }) => (
  <>
    <Toaster
      position="top-center"
      toastOptions={{
        duration: 4000,
      }}
    />
    {!isOnline && (
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: '#1a1a2e',
        borderTop: `1px solid ${theme.danger}`,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        fontSize: '13px',
        fontWeight: 600,
        color: theme.danger,
        fontFamily: "'Inter', sans-serif",
      }}>
        <span style={{ fontSize: '16px' }}>⚠</span>
        You're offline — your workout data is being saved locally
      </div>
    )}
  </>
);

function App() {
  // Detected synchronously before any async auth call — Supabase may clear the hash
  // before getSession() resolves, so we must capture it at component init time.
  const isRecoveryFlow = useRef(
    window.location.hash.includes('type=recovery') ||
    new URLSearchParams(window.location.search).get('type') === 'recovery'
  );
  const isPaymentPolling = useRef(false);

  // `wantedView` is the screen that was asked for: by the account check, by a button, or by the
  // address (Back and Forward). `standing` is what the last account check concluded: 'in', the
  // name of a gate screen such as 'paywall', or null. `view`, the screen that is drawn and that
  // the address follows, is the asked-for screen passed through that conclusion, so no caller of
  // setView can open a signed-in screen for somebody the check did not let in (utils/viewGate.js).
  const [wantedView, setView] = useState('loading'); // 'loading', 'home', 'onboarding', 'login', 'dashboard', 'admin', 'reset-password', 'stats'
  const [standing, setStanding] = useState(null);
  const [statsTab, setStatsTab] = useState(undefined); // the Stats tab to open on, when not Overview
  const view = gateView(wantedView, standing);
  // For the account check and sign-out only: go to a screen and record what that says about the account.
  const settle = (v) => {
    setStanding(standingFor(v));
    setView(v);
  };
  // A request that was refused is forgotten, so it cannot come true later by itself.
  useEffect(() => {
    if (wantedView !== view) setView(view);
  }, [wantedView, view]);
  const [user, setUser] = useState(null);
  const [homeGoogleLoading, setHomeGoogleLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  // The state of a bundle bought in Nutrition, for the failed-payment notice (Codex's review 038, G3)
  const [bundleBilling, setBundleBilling] = useState(null);
  const [showSuspendedSupport, setShowSuspendedSupport] = useState(false);
  const [paymentPolling, setPaymentPolling] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false); // "Logging out…" while the server is told (up to a few seconds)

  // One-time slide-in reveal for the first real screen after the boot loading
  // state — never replays on later in-app navigation (e.g. dashboard -> stats
  // -> dashboard). See RevealOnMount in components/LoadingScreen.jsx.
  const firstRevealDone = useRef(false);
  const [revealActive, setRevealActive] = useState(false);
  useEffect(() => {
    if (view !== 'loading' && view !== 'unreachable' && !firstRevealDone.current) {
      firstRevealDone.current = true;
      setRevealActive(true);
      const t = setTimeout(() => setRevealActive(false), 500);
      return () => clearTimeout(t);
    }
  }, [view]);

  // The person is signed in (a session is saved on this device) but the server did not answer, so
  // their account could not be loaded. Not the welcome screen: they have not been signed out.
  const showCannotReach = () => setView((current) => (WORKING_VIEWS.has(current) ? current : 'unreachable'));

  const navigate = useNavigate();
  const location = useLocation();

  // Get user role and permissions
  const {
    role, status, canAccessAdmin, isActive,
    loading: roleLoading, unreachable: roleUnreachable, refetch: refetchRole, forget: forgetRole,
  } = useUserRole();

  // Wraps setView for user-initiated navigation. `view` stays the single source of truth for
  // the async auth/payment logic below — this just keeps the name call sites already use.
  // The actual URL push happens in the view->URL sync effect further down.
  const navigateTo = (v) => {
    setStatsTab(undefined);
    setView(v);
  };

  // After a workout's summary: Stats opens on Progress, where Training load shows what was just done
  // (owner, 23 October 2026), instead of going straight on to the next workout's energy check.
  const showProgressAfterWorkout = () => {
    setStatsTab('progress');
    setView('stats');
  };

  // Keeps the URL in sync with `view` (view changed -> push a real history entry) and vice
  // versa (Back/Forward changed the URL -> update `view`). Comparing the current URL against
  // what `view` would produce is what distinguishes "the app navigated" from "the user pressed
  // Back/Forward" without needing an extra flag — whichever effect runs and finds them already
  // matching is a no-op.
  //
  // Both stand down entirely while view === 'loading': that's the async auth-resolution window
  // (checkUser/loadUserProfile/payment polling above), and it must be the only writer of `view`
  // during that time — restoreToDashboard() already reads window.location.pathname directly to
  // decide the landing view once auth confirms. Letting effectB *also* translate the URL into
  // `view` during this window raced against that resolution (deep-linking to any known path
  // while logged out would fight with checkUser's setView('home') every render, each toggling
  // the other — caught via Playwright before this shipped, see cipher build log).
  //
  // 'unreachable' is the same window, still open: auth has not resolved, only failed to reach the
  // server so far. It has no URL of its own, so the address stays what it was and
  // restoreToDashboard() finds it when a retry works; and Back or Forward must not open a
  // signed-in screen before the account has been loaded.
  useEffect(() => {
    if (view === 'loading') return;
    const expectedPath = VIEW_TO_PATH[view];
    if (expectedPath && expectedPath !== location.pathname) {
      navigate(expectedPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  useEffect(() => {
    if (view === 'loading' || view === 'unreachable') return;
    const expectedPath = VIEW_TO_PATH[view];
    if (expectedPath !== location.pathname) {
      const nextView = PATH_TO_VIEW[location.pathname];
      if (!nextView) return;
      if (gateView(nextView, standing) === nextView) setView(nextView);
      // Refused: the screen stays, so the address is put back to say so.
      else if (expectedPath) navigate(expectedPath, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Called by loadUserProfile when auth confirms the user belongs on the dashboard.
  // Checks the current URL for a previously visited/deep-linked restorable view and goes
  // there instead. Read directly at call time, before any navigate() from this render has
  // fired, so it still reflects whatever the user refreshed or deep-linked to. Auth always
  // wins — this is only consulted after Supabase confirms active status.
  const restoreToDashboard = () => {
    clearJoinIntent(); // a member now: no longer joining
    const current = PATH_TO_VIEW[window.location.pathname];
    settle(current && RESTORABLE_VIEWS.has(current) ? current : 'dashboard');
  };

  // App-wide offline indicator — uses Capacitor Network on device, web events in browser
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Native: use Capacitor Network plugin (more reliable than navigator.onLine)
      Network.getStatus().then(s => setIsOnline(s.connected));
      const listener = Network.addListener('networkStatusChange', s => setIsOnline(s.connected));
      return () => { listener.then(l => l.remove()); };
    } else {
      // Web: use browser events
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, []);

  // Keep Sentry user context in sync — every error report will carry who was logged in
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Push notifications are handled by the one service worker registered in index.js
  // (/service-worker.js, which loads /sw.js). Registering /sw.js here as well made the two take
  // turns at the same scope; see the note at the top of public/service-worker.js.

  // Check authentication state on mount and listen for auth changes
  useEffect(() => {
    // Handle cross-app session transfer from Cipher Nutrition.
    // Read from window.location.search directly — captured before any React re-render.
    const urlParams = new URLSearchParams(window.location.search);
    const tokenHash = urlParams.get('token_hash');
    const tokenType = urlParams.get('type');
    const accessToken = urlParams.get('access_token');
    const refreshToken = urlParams.get('refresh_token');

    if (accessToken && refreshToken) {
      // Fast path: setSession fires SIGNED_IN instantly, no verifyOtp round-trip.
      window.history.replaceState({}, '', window.location.pathname);
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ data, error }) => {
          if (error) { console.error('[cross-app] setSession failed:', error.message); checkUser(); return; }
          if (data?.user) loadUserProfile(data.user);
          else checkUser();
        })
        .catch((err) => { console.error('[cross-app] setSession threw:', err.message); checkUser(); });
    } else if (tokenHash && tokenType === 'magiclink') {
      window.history.replaceState({}, '', window.location.pathname);
      supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' })
        .then(({ error }) => {
          if (error) console.error('[cross-app] verifyOtp failed:', error.message);
          checkUser();
        })
        .catch((err) => {
          console.error('[cross-app] verifyOtp threw:', err.message);
          checkUser();
        });
    } else if (window.opener) {
      // Opened as a true popup from Nutrition with no URL tokens (postMessage path) —
      // request the session via postMessage instead, mirroring Nutrition's own
      // desktop-opener fallback in app/_layout.tsx. checkUser() still runs as the
      // normal fallback below if no reply arrives.
      for (const origin of NUTRITION_ORIGINS) {
        try {
          window.opener.postMessage({ type: 'REQUEST_CIPHER_SESSION' }, origin);
        } catch {}
      }
      checkUser();
    } else {
      checkUser();
    }

    // Cross-app session handoff via postMessage (Nutrition -> Fitness). Accept from
    // any known Nutrition origin — tokens are Supabase JWTs validated server-side,
    // so accepting from an unknown origin is safe: invalid tokens simply fail setSession.
    const handleCrossAppMessage = (event) => {
      if (!NUTRITION_ORIGINS.includes(event.origin)) return;
      if (event.data?.type !== 'CIPHER_SESSION') return;
      const { access_token, refresh_token } = event.data;
      supabase.auth.setSession({ access_token, refresh_token })
        .then(({ data, error }) => {
          if (error) { console.error('[cross-app] postMessage setSession failed:', error.message); return; }
          if (data?.user) loadUserProfile(data.user);
        })
        .catch((err) => console.error('[cross-app] postMessage setSession threw:', err.message));
    };
    window.addEventListener('message', handleCrossAppMessage);

    // onAuthStateChange handles post-load events: logout, password reset, and
    // the SIGNED_OUT → SIGNED_IN cycle that happens when a device wakes from sleep
    // with an expired token (fixes spurious logout flashes on iPad/desktop).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        isRecoveryFlow.current = true;
        setView('reset-password');
      } else if (event === 'SIGNED_IN' && session?.user && !isRecoveryFlow.current) {
        // Only handle SIGNED_IN after initial load — prevents double-call on mount.
        // This fires when a device recovers from a sleep/wake token refresh cycle.
        // Guard also blocks the SIGNED_IN that Supabase fires alongside PASSWORD_RECOVERY.
        if (view !== 'loading') {
          await loadUserProfile(session.user);
        }
      } else if (event === 'SIGNED_OUT') {
        // Signed out, perhaps in another tab: forget the person as well as the screen, or "Sign up"
        // here carried on as them, skipping the account step (Codex's task 019, R1)
        isRecoveryFlow.current = false;
        accountEpoch.current += 1;
        setUser(null);
        setUserProfile(null);
        setBundleBilling(null);
        settle('home');
      }
    });

    return () => {
      subscription?.unsubscribe();
      window.removeEventListener('message', handleCrossAppMessage);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The check now under way, if any: the "can't reach the server" screen's retries join it rather
  // than start another alongside.
  const checkUnderWay = useRef(null);

  // Moves on at every sign-out, here or in another tab. An account check that began before it is
  // answering about a person who has gone, and is ignored (Codex's task 019, R1).
  const accountEpoch = useRef(0);

  const checkUser = () => {
    const tooLong = setTimeout(showCannotReach, CANNOT_REACH_AFTER_MS);
    const check = findWhoIsSignedIn().finally(() => {
      clearTimeout(tooLong);
      if (checkUnderWay.current === check) checkUnderWay.current = null;
    });
    checkUnderWay.current = check;
    return check;
  };

  const findWhoIsSignedIn = async () => {
    try {
      // Password recovery flow — let onAuthStateChange handle the PASSWORD_RECOVERY
      // event and show the reset form. If we call loadUserProfile here, the user
      // gets logged straight in and never sees the new-password screen.
      if (isRecoveryFlow.current) return;

      markStartup(STEPS.signIn);
      const { data: { session }, error } = await supabase.auth.getSession();
      if (session?.user) {
        await loadUserProfile(session.user);
      } else if (authCouldNotReach(error)) {
        // A session is saved on this device; its token had run out and could not be renewed
        // because the server did not answer. The session is still there.
        showCannotReach();
      } else {
        settle('home');
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      if (authCouldNotReach(error)) showCannotReach();
      else settle('home');
    }
  };

  // Handle Stripe redirect back to app after checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success') {
      window.history.replaceState({}, '', '/');
      isPaymentPolling.current = true;
      setPaymentPolling(true);
    }
  }, []);

  // Poll DB + Stripe directly after payment until subscription is confirmed
  useEffect(() => {
    if (!paymentPolling || !user) return;
    let attempts = 0;
    // Another update for this member held their turn (api/lib/billingSync.js): not an answer yet, so the
    // polling carries on past its usual 25 tries, to at most 60 (Codex's review 041, D4). It stays
    // unanswered, across ticks and through failed requests, until a definite answer comes (review 042, R2).
    let pending = false;
    // One check at a time: the next starts 2 seconds after the last one ends. An answer that arrives after
    // the run has stopped (finished, closed, signed out or another account) changes nothing, so an older
    // "no" cannot undo a newer "paid" (Codex's recheck 043, N1).
    let stopped = false;
    let timer;
    const epoch = accountEpoch.current;
    const sameAccount = () => accountEpoch.current === epoch;
    const current = () => !stopped && sameAccount();
    const tick = async () => {
      attempts++;
      try {
        // Check users table (subscription_status updated by Fitness webhook)
        const { data: profile } = await supabase
          .from('users')
          .select('subscription_status, is_complimentary, complimentary_until, training_location')
          .eq('auth_user_id', user.id)
          .single();
        if (!current()) return;
        const today = new Date().toISOString().split('T')[0];
        const compActive = profile?.is_complimentary && (!profile.complimentary_until || profile.complimentary_until >= today);
        let hasAccess = compActive || statusGivesAccess(profile?.subscription_status);

        // Also check subscriptions table for bundle access (Nutrition upgrade path).
        // This uses the client JWT — if RLS allows it, we get a fast result.
        if (!hasAccess) {
          const { data: bundleSub } = await supabase
            .from('subscriptions')
            .select('tier, status')
            .eq('user_id', user.id)
            .maybeSingle();
          if (!current()) return;
          if (bundleSub?.tier === 'bundle' && statusGivesAccess(bundleSub.status)) {
            hasAccess = true;
          }
        }

        // Every 3rd attempt (or on final attempt) also verify directly via Stripe
        // This self-heals if the webhook is slow or misconfigured
        if (!hasAccess && (attempts % 3 === 0 || attempts >= 25)) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token) {
              const res = await fetch('/api/verify-subscription', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
              });
              if (res.ok) {
                const data = await res.json();
                if (!current()) return;
                if (data.hasAccess) hasAccess = true;
                if (data.pending) pending = true;
                else if (data.hasAccess === false) pending = false; // a definite "no"
              }
            }
          } catch {
            // non-fatal
          }
        }
        if (!current()) return;

        if (hasAccess || (attempts >= 25 && !pending) || attempts >= 60) {
          stopped = true;
          isPaymentPolling.current = false;
          setPaymentPolling(false);
          if (!hasAccess && pending) {
            // Still not answered: say so (with Try again) rather than offer another purchase
            showCannotReach();
            return;
          }
          if (hasAccess) {
            // Re-fetch profile to get fresh training_location after self-heal
            const { data: freshProfile } = await supabase
              .from('users')
              .select('training_location')
              .eq('auth_user_id', user.id)
              .single();
            if (!sameAccount()) return;
            if (freshProfile?.training_location) restoreToDashboard();
            else settle('fitness-bridge');
          } else {
            settle('paywall');
          }
          return;
        }
      } catch {
        // keep polling
      }
      if (current()) timer = setTimeout(tick, 2000);
    };
    timer = setTimeout(tick, 2000);
    return () => { stopped = true; clearTimeout(timer); };
    // restoreToDashboard only calls state setters, so an older copy of it does the same thing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentPolling, user]);


  const loadUserProfile = async (authUser) => {
    markStartup(STEPS.account);
    // Each wait below may end after a sign-out: then nothing of this person is put back
    const epoch = accountEpoch.current;
    const signedOutSince = () => epoch !== accountEpoch.current;
    try {
      let { data: profile, error, status: answered } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single();

      // A transient empty read (auth token still propagating, replica lag) is
      // indistinguishable from "this person has no account" and would wrongly
      // route an existing client into onboarding. Retry once before believing it.
      if (error?.code === 'PGRST116') {
        await new Promise(r => setTimeout(r, 700));
        ({ data: profile, error, status: answered } = await supabase
          .from('users')
          .select('*')
          .eq('auth_user_id', authUser.id)
          .single());
      }
      if (signedOutSince()) return;

      if (error) {
        if (error.code === 'PGRST116') {
          // No fitness profile yet — check if they have a bundle subscription
          const subRead = await supabase
            .from('subscriptions')
            .select('tier, status')
            .eq('user_id', authUser.id)
            .single();
          if (signedOutSince()) return;
          // "Has no subscription" and "the server did not say" lead to different places.
          if (couldNotReach(subRead)) { showCannotReach(); return; }
          const sub = subRead.data;
          const hasBundleAccess = sub?.tier === 'bundle' && statusGivesAccess(sub.status);
          if (hasBundleAccess) {
            // Auto-create their fitness profile and send them through bridge setup
            await supabase.from('users').upsert({
              auth_user_id: authUser.id,
              email: authUser.email,
              name: authUser.user_metadata?.full_name || authUser.email?.split('@')[0],
              role: 'client',
              status: 'active',
              approved_at: new Date().toISOString(),
            }, { onConflict: 'auth_user_id', ignoreDuplicates: true });
            if (signedOutSince()) return;
            setUser(authUser);
            setUserProfile(null);
            setBundleBilling(null);
            settle('fitness-bridge');
          } else {
            // No bundle — but this auth account might still belong to an existing
            // Nutrition-only user (has a `profiles` row, just never bought fitness
            // access) vs. someone genuinely new to Cipher entirely (e.g. a
            // first-time Google/Apple sign-in with no history in either app).
            // Only the former should land on the paywall — the latter has never
            // seen the onboarding questionnaire at all and needs to go through it.
            const nutritionRead = await supabase
              .from('profiles')
              .select('id')
              .eq('id', authUser.id)
              .maybeSingle();
            if (signedOutSince()) return;
            // Unanswered, this would send an existing Nutrition user through sign-up again.
            if (couldNotReach(nutritionRead)) { showCannotReach(); return; }
            const nutritionProfile = nutritionRead.data;
            setUser(authUser);
            setUserProfile(null);
            setBundleBilling(null);
            if (nutritionProfile && nutritionJoinedOnThisDevice() && !wantsToJoin()) {
              // Someone with Nutrition only (plan 005, joined for everyone since 16 October 2026): their
              // app is Nutrition, on this same address; it offers the bundle from there. A device that
              // chose the separate app (?nutrition=separate) gets the payment screen, as before
              goToJoinedNutrition();
            } else if (nutritionProfile) {
              settle('paywall');
            } else {
              settle('onboarding');
            }
          }
        } else if (couldNotReach({ error, status: answered })) {
          // The server did not answer. The person is signed in; their account is not loaded yet.
          console.error('Could not reach the server to load the profile:', error);
          showCannotReach();
        } else {
          console.error('Error loading profile:', error);
          settle('home');
        }
        return;
      }

      setUser(authUser);
      setUserProfile(profile);

      // Check account status
      if (profile.status === 'pending') {
        settle('pending-approval');
      } else if (profile.status === 'suspended') {
        settle('suspended');
      } else if (profile.status === 'rejected') {
        settle('rejected');
      } else if (profile.status === 'active') {
        // Route based on role
        if (profile.role === 'super_admin' || profile.role === 'admin' || profile.role === 'coach') {
          settle('admin');
        } else {
          // Clients need fitness subscription, complimentary access, or a bundle subscription
          const today = new Date().toISOString().split('T')[0];
          const compActive = profile.is_complimentary === true &&
            (!profile.complimentary_until || profile.complimentary_until >= today);
          const hasFitnessAccess = compActive || statusGivesAccess(profile.subscription_status);

          if (hasFitnessAccess) {
            setBundleBilling(null); // their own Fitness subscription (or complimentary) gives access
            // If fitness setup is incomplete (bridge user), collect training details first
            if (profile.training_location) restoreToDashboard();
            else settle('fitness-bridge');
          } else {
            // Check for bundle subscription from Cipher Nutrition
            const subRead = await supabase
              .from('subscriptions')
              .select('tier, status')
              .eq('user_id', authUser.id)
              .single();
            if (signedOutSince()) return;
            // Unanswered, this would show a paying bundle client the paywall.
            if (couldNotReach(subRead)) { showCannotReach(); return; }
            const sub = subRead.data;
            const hasBundleAccess = sub?.tier === 'bundle' && statusGivesAccess(sub.status);
            setBundleBilling(hasBundleAccess ? sub.status : null);
            if (hasBundleAccess) {
              if (profile.training_location) restoreToDashboard();
              else settle('fitness-bridge');
            } else if (isPaymentPolling.current) {
              settle('payment-confirming');
            } else {
              // Fallback: verify directly with Stripe in case the webhook missed this user.
              // This handles the case where someone paid, logged out, and logged back in.
              if (profile.stripe_customer_id) {
                try {
                  const { data: { session } } = await supabase.auth.getSession();
                  if (session?.access_token) {
                    // A failed request is { failed: true }, not "no access"
                    const ask = async () => {
                      try {
                        const res = await fetch('/api/verify-subscription', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                        });
                        return res.ok ? await res.json() : { failed: true };
                      } catch {
                        return { failed: true };
                      }
                    };
                    // "pending": another update for this member holds their turn; ask again for up to
                    // 75 seconds (a turn lasts 60). Once busy, a failed request is still unanswered;
                    // unanswered at the end means "can't reach" with Try again, never an offer to buy
                    // (Codex's reviews 041 D4 and 042 R2)
                    let data = await ask();
                    let unanswered = !!data?.pending;
                    for (let waited = 0; unanswered && waited < 75000; waited += 5000) {
                      await new Promise((resolve) => setTimeout(resolve, 5000));
                      if (signedOutSince()) return;
                      data = await ask();
                      unanswered = !!data?.pending || !!data?.failed;
                    }
                    if (signedOutSince()) return;
                    if (data?.hasAccess) {
                      if (profile.training_location) { restoreToDashboard(); return; }
                      else { settle('fitness-bridge'); return; }
                    }
                    // No answer at all is not "no membership" either
                    if (unanswered || data?.failed) { showCannotReach(); return; }
                  }
                } catch {
                  // fall through to paywall
                }
              }
              if (signedOutSince()) return;
              settle('paywall');
            }
          }
        }
      } else {
        restoreToDashboard();
      }
    } catch (error) {
      if (signedOutSince()) return;
      console.error('Error loading profile:', error);
      if (authCouldNotReach(error)) showCannotReach();
      else settle('home');
    }
  };

  // A few seconds at most, whether or not the server answers, and the session is gone from this
  // device either way (src/utils/signOut.js). So the welcome screen that follows is true.
  const handleLogout = async () => {
    accountEpoch.current += 1;
    setLoggingOut(true);
    const { serverTold, gaveUp } = await signOutOfThisDevice().finally(() => setLoggingOut(false));
    // The library was still waiting on the server when the time ran out. A fresh start leaves
    // nothing of the old sign-in behind to act on whoever signs in next.
    if (gaveUp) {
      restartApp();
      return;
    }
    // When the server was told, the library has said so to everything that listens. When it was
    // not, nothing has: the role is dropped by hand.
    if (!serverTold) forgetRole();
    setUser(null);
    setUserProfile(null);
    setBundleBilling(null);
    settle('home');
  };

  // "Log in with a different account" when the server cannot be reached: a real log-out first, so the
  // account check still under way cannot bring the old person back over the log-in screen, then log in
  // (Codex's task 019, R4)
  const switchAccount = async () => {
    await handleLogout();
    settle('login');
  };

  // Logging out can take a few seconds when the server is slow: say so, instead of a screen that
  // seems to ignore the tap (work list, small jobs)
  if (loggingOut) {
    return (
      <div role="status" style={{ minHeight: '100dvh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, fontFamily: "'Inter', sans-serif" }}>
        <img src={cipherLogo} alt="" style={{ width: 72, height: 72, borderRadius: 16, opacity: 0.9 }} />
        <div style={{ color: theme.text, fontSize: 16, fontWeight: 600 }}>Logging out…</div>
      </div>
    );
  }

  // Loading state - Only show loading screen on initial load, not during token refresh
  // If view is already set (not 'loading'), let the current view stay mounted
  if (view === 'loading') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <LoadingScreen />
      </>
    );
  }

  // Signed in, but the server did not answer. Keeps trying; never the welcome screen.
  if (view === 'unreachable') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <ServerUnreachable
        onRetry={() => checkUnderWay.current || checkUser()}
        onUseAnotherAccount={switchAccount}
        onReload={() => window.location.reload()}
      />
      </>
    );
  }

  // Home screen (not logged in)
  if (view === 'home') {
    const handleHomeGoogleSignIn = async () => {
      setHomeGoogleLoading(true);
      try {
        await signInWithGoogle();
      } catch (error) {
        console.error('Google sign-in error:', error);
        toastError(error.message || 'Google sign-in failed');
        setHomeGoogleLoading(false);
      }
    };
    const homeButtons = (
      <>
        <button className="cf-home-btn-primary" onClick={() => setView('onboarding')}>
          <span>👤</span> New Client - Sign Up <span className="cf-home-btn-arrow">›</span>
        </button>
        <button className="cf-home-btn-secondary" onClick={() => setView('login')}>
          <span>🔒</span> Log In <span className="cf-home-btn-arrow">›</span>
        </button>
        <button className="cf-home-btn-google" onClick={handleHomeGoogleSignIn} disabled={homeGoogleLoading}>
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.85.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.96 10.71a5.4 5.4 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l3-2.33z"/>
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z"/>
          </svg>
          {homeGoogleLoading ? 'Connecting…' : 'Continue with Google'}
        </button>
      </>
    );
    const homeIconRow = (
      <div className="cf-home-icon-row">
        <div><Target size={18} strokeWidth={1.8} /><span>FOCUS</span></div>
        <div><Dumbbell size={18} strokeWidth={1.8} /><span>TRAIN</span></div>
        <div><TrendingUp size={18} strokeWidth={1.8} /><span>PROGRESS</span></div>
        <div><Trophy size={18} strokeWidth={1.8} /><span>ACHIEVE</span></div>
      </div>
    );
    const homeFooter = (
      <>
        <div className="cf-home-legal">
          <button onClick={() => setView('privacy-policy')}>Privacy Policy</button>
          <span>|</span>
          <button onClick={() => setView('terms')}>Terms of Service</button>
        </div>
      </>
    );
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <RevealOnMount active={revealActive}>
      <div className="cf-home-wrap" style={{ fontFamily: "'Inter', sans-serif" }}>
        {/* Mobile (<768px): full-bleed portrait hero photo, content pinned to bottom */}
        <div className="cf-home-mobile" style={{ backgroundImage: `url(${loginHeroMobile})` }}>
          <div className="cf-home-mobile-scrim" />
          <div className="cf-home-mobile-content">
            <img src={cipherLogo} alt="Cipher" className="cf-home-icon" />
            <h1 className="cf-home-wordmark">CIPHER<span>FITNESS</span></h1>
            <p className="cf-home-tagline">Elite fitness coaching, personalised for you</p>
            <p className="cf-home-trial">New members start with a 7-day free trial</p>
            {homeButtons}
            {homeIconRow}
            {homeFooter}
          </div>
        </div>

        {/* Desktop (≥768px): wide landscape hero photo, content anchored right */}
        <div className="cf-home-desktop">
          <div className="cf-home-desktop-hero" style={{ backgroundImage: `url(${loginHeroDesktop})` }}>
            <div className="cf-home-desktop-scrim" />
            <div className="cf-home-desktop-content">
              <img src={cipherLogo} alt="Cipher" className="cf-home-icon" />
              <h1 className="cf-home-wordmark">CIPHER<span>FITNESS</span></h1>
              <p className="cf-home-tagline">Elite fitness coaching, personalised for you</p>
              <p className="cf-home-trial">New members start with a 7-day free trial</p>
              {homeButtons}
              {homeIconRow}
              {homeFooter}
            </div>
          </div>
        </div>
      </div>
      </RevealOnMount>
      </>
    );
  }

  // Onboarding flow
  if (view === 'onboarding') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <RevealOnMount active={revealActive}>
      <div>
        <button
          onClick={() => setView('home')}
          style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            padding: '10px 16px',
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            color: colors.text,
            fontSize: '14px',
            cursor: 'pointer',
            zIndex: 1000,
          }}
        >
          ← Home
        </button>
        <OnboardingFlow
          existingAuthUser={user}
          onGoToLogin={() => setView('login')}
          onComplete={(created) => {
            // null: the account had already finished sign-up and nothing was saved
            if (created !== null) toastSuccess('Account created! Setting up your dashboard…');
            checkUser();
          }}
        />
      </div>
      </RevealOnMount>
      </>
    );
  }

  // Login page
  if (view === 'login') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <LoginPage
        onHome={() => setView('home')}
        onLoginSuccess={async () => {
          await checkUser();
        }}
        onSignUp={() => setView('onboarding')}
      />
      </>
    );
  }

  // Fitness bridge setup — nutrition subscriber completing fitness profile
  if (view === 'fitness-bridge') {
    return (
      <>
        <ToastContainer isOnline={isOnline} />
        <FitnessBridgeSetup
          user={user}
          onComplete={() => loadUserProfile(user)}
          onLogout={handleLogout}
        />
      </>
    );
  }

  // Payment confirming — webhook hasn't updated DB yet, poll until it does
  if (view === 'payment-confirming' || (paymentPolling && view !== 'dashboard' && view !== 'fitness-bridge')) {
    return (
      <div style={{ minHeight: '100dvh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, fontFamily: "'Inter', sans-serif" }}>
        <img src={cipherLogo} alt="Cipher" style={{ width: 72, height: 72, borderRadius: 16 }} />
        <div style={{ color: theme.text, fontSize: 18, fontWeight: 700 }}>Payment received!</div>
        <div style={{ color: theme.textMuted, fontSize: 14 }}>Setting up your account…</div>
        <div style={{ width: 36, height: 36, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: theme.gold, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Paywall — client has no active subscription
  if (view === 'paywall') {
    return (
      <RevealOnMount active={revealActive}>
        <PaymentGate
          userId={user?.id}
          email={user?.email}
          name={userProfile?.name}
          onLogout={handleLogout}
        />
      </RevealOnMount>
    );
  }

  // Client Dashboard
  if (view === 'dashboard') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <DesktopTopNav currentView="dashboard" userName={userProfile?.name} onNavigate={navigateTo} onLogout={handleLogout} />
      <RevealOnMount active={revealActive}>
        <ClientProgramView
          guideRole={role}
          userName={userProfile?.name}
          onShowProgress={showProgressAfterWorkout}
          topNotice={(
            <SubscriptionEndingNotice
              // The notice follows the subscription that gives access now: a bundle row when that is what
              // lets them in, whatever an old Fitness subscription left on the account (Codex's review 039,
              // R3), or else their own Fitness subscription. Cards are updated in one place: Settings,
              // "Manage subscription" (one payment system, owner 24 October 2026).
              notice={subscriptionEndingNotice(
                bundleBilling ? { ...userProfile, subscription_status: bundleBilling } : userProfile
              )}
              onDownload={() => navigateTo('privacy')}
              onUpdateCard={() => navigateTo('privacy')}
            />
          )}
        />
      </RevealOnMount>
      <PushNotificationPrompt />
      {/* Asked once: may the coach see health answers and body scans (migration 083) */}
      <HealthSharingPrompt userId={userProfile?.auth_user_id} />
      <BottomNav currentView="dashboard" onNavigate={navigateTo} />
      </>
    );
  }

  // Client Stats
  if (view === 'stats') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <DesktopTopNav currentView="stats" userName={userProfile?.name} onNavigate={navigateTo} onLogout={handleLogout} />
      <EnhancedStats
        userId={userProfile?.auth_user_id}
        onBack={() => navigateTo('dashboard')}
        initialTab={statsTab}
      />
      <BottomNav currentView="stats" onNavigate={navigateTo} />
      </>
    );
  }


  // Coach <-> Client Messages
  if (view === 'messages') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <DesktopTopNav currentView="messages" userName={userProfile?.name} onNavigate={navigateTo} onLogout={handleLogout} />
      <CoachMessagesScreen
        userId={user?.id}
        onBack={() => navigateTo('dashboard')}
      />
      <BottomNav currentView="messages" onNavigate={navigateTo} />
      </>
    );
  }

  // Legal pages
  if (view === 'privacy-policy') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <div style={{ minHeight: '100dvh', background: colors.bg, fontFamily: "'Inter', sans-serif" }}>
        <PrivacyPolicy onBack={() => setView(user ? 'dashboard' : 'home')} />
      </div>
      </>
    );
  }

  if (view === 'terms') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <div style={{ minHeight: '100dvh', background: colors.bg, fontFamily: "'Inter', sans-serif" }}>
        <TermsOfService onBack={() => setView(user ? 'dashboard' : 'home')} />
      </div>
      </>
    );
  }

  // Privacy Settings
  if (view === 'privacy') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <div style={{ minHeight: '100dvh', background: colors.bg, fontFamily: "'Inter', sans-serif" }}>
        <PrivacySettings
          guideRole={role}
          onBack={() => setView('dashboard')}
          userId={user?.id}
          onLogout={handleLogout}
        />
      </div>
      </>
    );
  }

  // Admin panel - PROTECTED
  if (view === 'admin') {
    // The server did not answer when asked for this person's role, and it was not known before.
    // That is not "no permission". (A role that was known is kept: see useUserRole.)
    if (!canAccessAdmin && roleUnreachable) {
      return (
        <>
        <ToastContainer isOnline={isOnline} />
        <ServerUnreachable onRetry={() => refetchRole(user)} onUseAnotherAccount={switchAccount} onReload={() => window.location.reload()} />
        </>
      );
    }

    // Check if user has permission to access admin panel
    // Don't kick out user if role is still loading (prevents unmounting during token refresh)
    if (!canAccessAdmin && !roleLoading) {
      return (
        <>
        <ToastContainer isOnline={isOnline} />
        <div style={{
          minHeight: '100dvh',
          background: colors.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: "'Inter', sans-serif",
        }}>
          <div style={{
            maxWidth: '500px',
            background: colors.bgCard,
            padding: '40px',
            borderRadius: '20px',
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🚫</div>
            <h1 style={{
              fontSize: '28px',
              fontWeight: 700,
              color: colors.text,
              marginBottom: '16px',
            }}>
              Access Denied
            </h1>
            <p style={{
              fontSize: '16px',
              color: theme.textMuted,
              marginBottom: '24px',
              lineHeight: 1.6,
            }}>
              You don't have permission to access the admin panel.
              {role === 'client' && ' This area is for coaches and administrators only.'}
              {!isActive && status === 'pending' && ' Your account is pending approval.'}
            </p>
            <button
              onClick={() => setView('home')}
              style={{
                width: '100%',
                padding: '16px',
                background: `linear-gradient(135deg, ${colors.primary}, ${theme.goldPressed})`,
                border: 'none',
                borderRadius: '14px',
                color: theme.bg,
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ← Back to Home
            </button>
          </div>
        </div>
        </>
      );
    }

    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <AdminPanel
        onLogout={handleLogout}
      />
      </>
    );
  }

  // Pending Approval
  if (view === 'pending-approval') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <RevealOnMount active={revealActive}>
        <ApprovalPending
          userName={userProfile?.name}
          userEmail={userProfile?.email}
        />
      </RevealOnMount>
      </>
    );
  }

  // Suspended Account
  if (view === 'suspended') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <RevealOnMount active={revealActive}>
      <div style={{
        minHeight: '100dvh',
        background: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: "'Inter', sans-serif",
      }}>
        <div style={{
          maxWidth: '500px',
          background: colors.bgCard,
          padding: '40px',
          borderRadius: '20px',
          border: `1px solid ${colors.border}`,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>⛔</div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: colors.text,
            marginBottom: '16px',
          }}>
            Account Suspended
          </h1>
          <p style={{
            fontSize: '16px',
            color: theme.textMuted,
            marginBottom: '24px',
            lineHeight: 1.6,
          }}>
            Your account has been suspended. If you'd like to reactivate it, get in touch below.
          </p>
          <button
            onClick={() => setShowSuspendedSupport(true)}
            style={{
              width: '100%',
              padding: '16px',
              background: theme.gold,
              border: 'none',
              borderRadius: '14px',
              color: theme.bg,
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              marginBottom: '12px',
            }}
          >
            Contact Us to Reactivate
          </button>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '16px',
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '14px',
              color: colors.text,
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Log Out
          </button>
        </div>
      </div>
      </RevealOnMount>
      {showSuspendedSupport && (
        <SupportModal
          userEmail={userProfile?.email}
          onClose={() => setShowSuspendedSupport(false)}
        />
      )}
      </>
    );
  }

  // Rejected Account
  if (view === 'rejected') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <RevealOnMount active={revealActive}>
      <div style={{
        minHeight: '100dvh',
        background: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: "'Inter', sans-serif",
      }}>
        <div style={{
          maxWidth: '500px',
          background: colors.bgCard,
          padding: '40px',
          borderRadius: '20px',
          border: `1px solid ${colors.border}`,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>❌</div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: colors.text,
            marginBottom: '16px',
          }}>
            Account Not Approved
          </h1>
          <p style={{
            fontSize: '16px',
            color: theme.textMuted,
            marginBottom: '24px',
            lineHeight: 1.6,
          }}>
            Your account application was not approved. Please contact the administrator if you believe this is an error.
          </p>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '16px',
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '14px',
              color: colors.text,
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Log Out
          </button>
        </div>
      </div>
      </RevealOnMount>
      </>
    );
  }

  // Password reset (from email link)
  if (view === 'reset-password') {
    return (
      <>
      <ToastContainer isOnline={isOnline} />
      <ResetPassword
        onComplete={async () => {
          await supabase.auth.signOut();
          settle('login');
        }}
      />
      </>
    );
  }

  return null;
}

export default AppRouter;
